import { PARTICIPANT_TYPE } from '@/configs'
import dayjs from '@/lib/dayjs'
import { DayjsProperty } from '@/lib/jackson'
import { Dayjs } from 'dayjs'
import { JsonProperty, Serializable } from 'ts-jackson'
import { Member } from '../member/model'
import { Fromable } from '../model'
import { ROOM_SPOT_KIND } from '../room/model'

export enum CLASS_SCHEDULE_LIST_STATUS {
  ALL = 'all',
  ACTIVE = 'active',
  UPCOMING = 'upcoming',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export enum SCHEDULE_TYPE {
  NOW = 'open-now',
  UPCOMING = 'upcoming',
}

export enum EXCHANGE {
  FREE = 'free-class',
  CREDIT = 'credit-exchange',
}

export enum CHARGE_TYPE {
  FREE = 'FREE',
  USE_SESSION = 'USE_SESSION',
  CREDIT = 'CREDIT',
  /** Real money. `price` on the pricing row is BAHT (credits everywhere else). */
  PAID = 'PAID',
}

/**
 * Settlement state of a PAID class booking. `null` on every row that involves no
 * money at all — a chip must not be rendered for those.
 */
export enum CLASS_BOOKING_PAYMENT_STATUS {
  /** Seat held, member has not paid yet (15-minute hold). */
  PENDING = 'PENDING',
  /** Member uploaded a transfer slip; staff must approve or reject it. */
  VERIFYING = 'VERIFYING',
  PAID = 'PAID',
  /** Slip rejected — the member may upload another one. */
  REJECTED = 'REJECTED',
  /** Booking was cancelled before it was ever paid. */
  CANCELLED = 'CANCELLED',
  /**
   * The receipt was voided. The money went back but the SEAT STAYS — cancelling
   * the booking is a separate decision. Staff may collect again from here.
   */
  REFUNDED = 'REFUNDED',
}

/** Verdict of the automatic (EasySlip) check run on an uploaded transfer slip. */
export const SLIP_VERIFICATION_OUTCOME = {
  /** Everything matched. The booking was auto-approved and arrives PAID. */
  VERIFIED: 'VERIFIED',
  /** Slip read, but the amount or the receiving account disagreed. */
  MISMATCH: 'MISMATCH',
  /** This transfer was already used to pay for something. */
  DUPLICATE: 'DUPLICATE',
  /** The provider could not be reached or answered with something unusable. */
  ERROR: 'ERROR',
  /** No automatic check ran (e.g. the club has no API key). */
  SKIPPED: 'SKIPPED',
} as const

export type SlipVerificationOutcome =
  (typeof SLIP_VERIFICATION_OUTCOME)[keyof typeof SLIP_VERIFICATION_OUTCOME]

/**
 * Latest automatic slip check attached to a booking.
 *
 * A plain interface on purpose: under Vite a nested `@JsonProperty` model is
 * NOT instantiated as a class, so this must never grow methods — read it with
 * the helpers in `features/class/utils/slipVerification`.
 */
export interface ClassBookingSlipVerification {
  outcome: SlipVerificationOutcome
  /** Machine codes (`AMOUNT_MISMATCH`, `NO_API_KEY`, …) — never user-facing text. */
  reasons: string[]
  transRef: string | null
  /** Baht read off the slip. */
  amount: number | null
  senderName: string | null
  /** ISO timestamp printed on the slip. */
  slipDate: string | null
  /** ISO timestamp of the check itself. */
  checkedAt: string
}

/** How staff records the money for a PAID class booking. */
export const CLASS_BOOKING_PAYMENT_METHOD = {
  CASH: 'CASH',
  CARD: 'CARD',
  TRANSFER: 'TRANSFER',
} as const

export type ClassBookingPaymentMethod =
  (typeof CLASS_BOOKING_PAYMENT_METHOD)[keyof typeof CLASS_BOOKING_PAYMENT_METHOD]

/**
 * How a schedule's seats are divided between participant types.
 *
 * - `PER_TYPE` — each type owns its own seat count (the original behaviour).
 * - `SHARED` — one pool of `capacity` seats that every allowed type draws from.
 *   The backend still writes each allowed type's `capacityX` = the round total
 *   (0 for the types that are not allowed), so readers that only know about
 *   per-type capacity keep working.
 */
export type ScheduleCapacityMode = 'PER_TYPE' | 'SHARED'

/** Participant type the backend resolved (or would resolve) for a booking. */
export enum CLASS_BOOKING_MEMBER_TYPE {
  MEMBER = 'MEMBER',
  SESSION = 'SESSION',
  DROP_IN = 'DROP_IN',
}

/** Why `GET /bookings/quote` says the booking cannot go through. */
export enum CLASS_BOOKING_BLOCK_REASON {
  NEEDS_SESSION_PACKAGE = 'NEEDS_SESSION_PACKAGE',
  TYPE_NOT_ALLOWED = 'TYPE_NOT_ALLOWED',
  INSUFFICIENT_CREDITS = 'INSUFFICIENT_CREDITS',
  MEMBERSHIP_NOT_USABLE = 'MEMBERSHIP_NOT_USABLE',
  NO_DROP_IN = 'NO_DROP_IN',
  /** The class is not open to this member (visibility rules — see §7). */
  NOT_ELIGIBLE = 'NOT_ELIGIBLE',
}

/** Who may see and book a class in the member app. */
export const CLASS_VISIBILITY = {
  PUBLIC: 'PUBLIC',
  /** Only members holding a package explicitly linked to this class. */
  PACKAGE_ONLY: 'PACKAGE_ONLY',
  /** Only members carrying one of the class's allowed member tags. */
  TAG_ONLY: 'TAG_ONLY',
  /** Hidden from members entirely; staff book on their behalf. */
  STAFF_ONLY: 'STAFF_ONLY',
} as const

export type ClassVisibility = (typeof CLASS_VISIBILITY)[keyof typeof CLASS_VISIBILITY]

/** i18n key for a class-visibility label. */
export const CLASS_VISIBILITY_KEY: Record<ClassVisibility, string> = {
  [CLASS_VISIBILITY.PUBLIC]: 'class-visibility-public',
  [CLASS_VISIBILITY.PACKAGE_ONLY]: 'class-visibility-package-only',
  [CLASS_VISIBILITY.TAG_ONLY]: 'class-visibility-tag-only',
  [CLASS_VISIBILITY.STAFF_ONLY]: 'class-visibility-staff-only',
}

/**
 * A member tag a TAG_ONLY class is opened to. Plain object on purpose: nested
 * ts-jackson models arrive as plain data under Vite.
 */
export type ClassAllowedTag = {
  id: number
  label: string
  color: string | null
}

/**
 * How a booking came to be cancelled. `null` on every booking that was not
 * cancelled, and on rows cancelled before the column existed — which is why
 * the chip is skipped rather than defaulted.
 */
export const CLASS_CANCEL_KIND = {
  /** Member cancelled inside the notice window. */
  ON_TIME: 'ON_TIME',
  /** Member cancelled after the deadline (see the club's late-cancel policy). */
  LATE: 'LATE',
  STAFF: 'STAFF',
  /** The reaper / auto-cancel jobs. */
  SYSTEM: 'SYSTEM',
} as const

export type ClassCancelKind = (typeof CLASS_CANCEL_KIND)[keyof typeof CLASS_CANCEL_KIND]

/** i18n key for a cancel-kind chip. */
export const CLASS_CANCEL_KIND_KEY: Record<ClassCancelKind, string> = {
  [CLASS_CANCEL_KIND.ON_TIME]: 'class-cancel-kind-on-time',
  [CLASS_CANCEL_KIND.LATE]: 'class-cancel-kind-late',
  [CLASS_CANCEL_KIND.STAFF]: 'class-cancel-kind-staff',
  [CLASS_CANCEL_KIND.SYSTEM]: 'class-cancel-kind-system',
}

/** What a late cancel costs the member, as `GET /bookings/:id/cancel-preview` puts it. */
export const CLASS_CANCEL_CONSEQUENCE = {
  NONE: 'NONE',
  FORFEIT_SESSION: 'FORFEIT_SESSION',
  FORFEIT_CREDIT: 'FORFEIT_CREDIT',
  FORFEIT_PAYMENT: 'FORFEIT_PAYMENT',
  STRIKE: 'STRIKE',
} as const

export type ClassCancelConsequence =
  (typeof CLASS_CANCEL_CONSEQUENCE)[keyof typeof CLASS_CANCEL_CONSEQUENCE]

/** Why the preview says the member themselves could not cancel at all. */
export const CLASS_CANCEL_BLOCK_REASON = {
  ALREADY_STARTED: 'ALREADY_STARTED',
  LATE_BLOCKED: 'LATE_BLOCKED',
} as const

export type ClassCancelBlockReason =
  (typeof CLASS_CANCEL_BLOCK_REASON)[keyof typeof CLASS_CANCEL_BLOCK_REASON]

/**
 * Response of `GET /bookings/:id/cancel-preview`.
 *
 * Staff are shown the MEMBER's consequence, not their own: a staff cancel can
 * always go through, so the point of the dialog is to say what the member is
 * about to lose before someone presses the button for them.
 */
@Serializable()
export class ClassBookingCancelPreview extends Fromable {
  /** ISO timestamp of `startTime − cancelBefore`. */
  @JsonProperty()
  deadline!: string

  @JsonProperty()
  isLate!: boolean

  @JsonProperty()
  mode!: string

  @JsonProperty()
  consequence!: ClassCancelConsequence

  /** Late cancels already recorded in the current auto-ban window. */
  @JsonProperty()
  strikeCount!: number

  @JsonProperty()
  absenceThreshold!: number

  @JsonProperty()
  blockReason?: ClassCancelBlockReason | null
}

/** Body of `PATCH /bookings/:id/cancel`. Staff only may send `refund`. */
export type CancelClassBookingPayload = {
  /** Default true on the backend — send false to cancel without giving anything back. */
  refund?: boolean
  reason?: string
}

export type BookingClassPayload = {
  userId: string | number
  scheduleId: string | number
  userMembershipId?: string | number
  /**
   * Staff-only, PAID rows only. Given → the booking is settled immediately and a
   * receipt is issued. Omitted → the booking is created "รอชำระ": the seat is
   * held for 15 minutes and the member pays from LINE.
   */
  paymentMethod?: ClassBookingPaymentMethod
  /**
   * Pick-a-spot rooms only. Omitted → the backend auto-assigns the first free
   * seat by sort order, which is also what every room without a map does.
   */
  spotId?: string
}

/** The seat a booking holds. Plain data — nested models are plain under Vite. */
export type ClassBookingSpot = {
  id: string
  label: string
}

/** What one cell of a schedule's seat map is doing right now. */
export enum SCHEDULE_SPOT_STATUS {
  FREE = 'FREE',
  TAKEN = 'TAKEN',
  BLOCKED = 'BLOCKED',
  INSTRUCTOR = 'INSTRUCTOR',
}

/**
 * One cell of the seat map for a given round. `booking` is only filled for
 * staff callers — members get TAKEN without a name. Plain type: ts-jackson
 * nested arrays are plain objects under Vite.
 */
export type ScheduleSpot = {
  id: string
  label: string
  row: number
  col: number
  kind: ROOM_SPOT_KIND
  status: SCHEDULE_SPOT_STATUS
  booking?: { id: number; memberName: string } | null
}

/**
 * Response of `GET /classes/:id/schedules/:scheduleId/spots`. A room without a
 * seat map answers `requiresSpotSelection: false` and an empty `spots`, which
 * is how every seat-map surface decides whether to render at all.
 */
@Serializable()
export class ScheduleSpots extends Fromable {
  @JsonProperty('requiresSpotSelection')
  requiresSpotSelection!: boolean

  @JsonProperty('rows')
  rows!: number | null

  @JsonProperty('cols')
  cols!: number | null

  @JsonProperty('spots')
  spots!: ScheduleSpot[]
}

/**
 * The package the quote would charge (USE_SESSION) or merely record (FREE /
 * CREDIT). Plain object on purpose: nested ts-jackson models come back as plain
 * objects under Vite, so nothing here may rely on being a class instance.
 */
export type ClassBookingQuoteMembership = {
  userMembershipId: number
  packageName?: string
  remainingUses?: number
  isUnlimit?: boolean
}

/** One branch bank account a member may transfer the class fee to. */
export type ClassBookingBankAccount = {
  bankName?: string
  accountName?: string
  accountNo?: string
  bankBranchName?: string
}

/**
 * Self-payment details for a PAID quote: where the member would transfer and how
 * long their seat is held. `null` for every non-PAID charge type. Plain object —
 * nested ts-jackson models arrive as plain data under Vite.
 */
export type ClassBookingQuotePayment = {
  bankAccounts?: ClassBookingBankAccount[]
  holdMinutes?: number
}

/**
 * Response of `GET /bookings/quote` — exactly what `POST /bookings` will do for
 * this (member, schedule) pair, resolved by the same backend code path. A
 * booking the backend would refuse still answers 200 with `canBook: false` plus
 * the reason, so the staff modal can explain it instead of failing on submit.
 */
@Serializable()
export class ClassBookingQuote extends Fromable {
  @JsonProperty()
  memberType!: CLASS_BOOKING_MEMBER_TYPE

  @JsonProperty()
  chargeType!: CHARGE_TYPE

  @JsonProperty()
  price!: number

  @JsonProperty()
  membership?: ClassBookingQuoteMembership | null

  /** Only populated when `chargeType === CREDIT`. */
  @JsonProperty()
  creditBalance?: number

  @JsonProperty()
  canBook!: boolean

  @JsonProperty()
  blockReason?: CLASS_BOOKING_BLOCK_REASON | null

  /**
   * Whether the MEMBER can see this class in their app (class visibility, §7).
   * A staff quote keeps `canBook: true` with `memberEligible: false` — staff may
   * book on their behalf, they just need to know the member never saw it.
   */
  @JsonProperty()
  memberEligible?: boolean

  /** Only populated when `chargeType === PAID`. */
  @JsonProperty()
  payment?: ClassBookingQuotePayment | null
}

/** Bank accounts the member can transfer to, or an empty list when there are none. */
export function getQuoteBankAccounts(quote?: ClassBookingQuote | null): ClassBookingBankAccount[] {
  return quote?.payment?.bankAccounts ?? []
}

/**
 * The membership id to send with the booking, or undefined when the charge does
 * not consume a package. Kept as a function rather than a method: `deserialize`
 * builds real instances, but nested values do not, and a quote that arrived as
 * a plain object would lose its methods.
 */
export function getQuoteUserMembershipId(quote?: ClassBookingQuote | null): number | undefined {
  if (!quote || quote.chargeType !== CHARGE_TYPE.USE_SESSION) return undefined
  return quote.membership?.userMembershipId
}

@Serializable()
export class ClassCategory {
  @JsonProperty()
  id!: number

  @JsonProperty()
  name!: string

  @JsonProperty()
  description!: string

  public getOption() {
    return {
      label: this.name,
      value: this.id,
    }
  }
}

@Serializable()
export class ClassImage {
  @JsonProperty()
  id!: number

  @JsonProperty()
  imagePath!: string
}

@Serializable()
export class ClassInfo extends Fromable {
  @JsonProperty()
  id!: number

  @JsonProperty()
  title!: string

  @JsonProperty()
  description!: string

  @JsonProperty()
  youtubeUrl!: string

  @JsonProperty()
  isActive!: boolean

  @DayjsProperty()
  createdAt!: Dayjs

  @DayjsProperty()
  updatedAt!: Dayjs

  @JsonProperty()
  categories!: (string | number)[]

  @JsonProperty({ elementType: ClassImage })
  classImage!: ClassImage[]

  @JsonProperty()
  classType!: string

  @JsonProperty()
  scheduleCount?: number

  /** Defaults to PUBLIC when the backend omits it (pre-migration rows). */
  @JsonProperty()
  visibility?: ClassVisibility

  /** Populated for TAG_ONLY classes; empty otherwise. */
  @JsonProperty()
  allowedTags?: ClassAllowedTag[]

  /**
   * Write-only: the tag ids a form picked. The API answers with `allowedTags`
   * (id + label + colour) but takes `allowedTagIds`, and a form only ever has
   * the ids — inventing empty labels to round-trip through `allowedTags` would
   * put blank chips on screen the moment anything read the object back.
   */
  allowedTagIds?: number[]

  /** Copied onto every schedule created for this class when it leaves them blank. */
  @JsonProperty()
  defaultMinParticipants?: number | null

  @JsonProperty()
  defaultMinDecisionMinutes?: number | null

  public getOption() {
    return {
      label: this.title,
      value: this.id,
    }
  }

  public serializeToCreate() {
    return {
      title: this.title,
      description: this.description,
      youtubeUrl: this.youtubeUrl,
      isActive: this.isActive,
      categoryIds: this.categories?.map(Number) ?? [],
      ...(this.visibility ? { visibility: this.visibility } : {}),
      // Only meaningful for TAG_ONLY, and the backend REQUIRES a non-empty list
      // there — sending it for the other modes would be noise it rejects.
      ...(this.visibility === CLASS_VISIBILITY.TAG_ONLY
        ? { allowedTagIds: this.allowedTagIds ?? (this.allowedTags ?? []).map((tag) => tag.id) }
        : {}),
      // null clears a default; undefined leaves it untouched.
      ...(this.defaultMinParticipants !== undefined
        ? { defaultMinParticipants: this.defaultMinParticipants }
        : {}),
      ...(this.defaultMinDecisionMinutes !== undefined
        ? { defaultMinDecisionMinutes: this.defaultMinDecisionMinutes }
        : {}),
    }
  }
}

@Serializable()
export class Trainer extends Fromable {
  @JsonProperty()
  id!: number

  @JsonProperty()
  code!: string

  @JsonProperty()
  firstName!: string

  @JsonProperty()
  lastName!: string

  @JsonProperty()
  nickname!: string

  @JsonProperty()
  gender!: string

  @JsonProperty()
  email!: string

  @JsonProperty()
  phone!: string
}

@Serializable()
export class TrainerWithAvailableTime extends Fromable {
  @JsonProperty()
  startTime!: Dayjs

  @JsonProperty()
  endTime!: Dayjs

  @JsonProperty({ type: Trainer })
  trainer!: Trainer
}

@Serializable()
export class SingleSession extends Fromable {
  @JsonProperty()
  appointmentDate!: Dayjs

  @JsonProperty()
  startTime!: Dayjs

  @JsonProperty()
  endTime!: Dayjs
}

@Serializable()
export class TimeDuration extends Fromable {
  @JsonProperty()
  dayOfWeekIndex!: number

  @JsonProperty()
  startTime!: Dayjs

  @JsonProperty()
  endTime!: Dayjs

  @JsonProperty()
  trainerId?: number
}

@Serializable()
export class RecurringSchedule extends Fromable {
  @JsonProperty()
  startDate!: Dayjs

  @JsonProperty()
  endDate!: Dayjs

  @JsonProperty({ elementType: TimeDuration })
  singleTimeDurations!: TimeDuration[]
}

@Serializable()
export class Booking extends Fromable {
  @JsonProperty()
  id!: number

  @JsonProperty()
  userId!: number

  @JsonProperty()
  scheduleId!: number

  @JsonProperty()
  status!: string

  @DayjsProperty()
  bookedAt!: Dayjs

  @DayjsProperty()
  cancelledAt?: Dayjs | null

  @JsonProperty({ type: Member })
  user!: Member
}

@Serializable()
export class SchedulePricing extends Fromable {
  @JsonProperty()
  memberType!: string

  @JsonProperty()
  capacity!: number

  @JsonProperty()
  price!: number

  @JsonProperty()
  chargeType?: CHARGE_TYPE
}

export type ScheduleByUserType = {
  price: number
  capacity: number
  bookingCount: number
  queueCount: number
  chargeType?: CHARGE_TYPE
}

@Serializable()
export class ClassBookingItem extends Fromable {
  @JsonProperty()
  memberId!: number

  @JsonProperty()
  userMembershipId!: number
}

/**
 * The two minimum-participants fields, only when they were actually set.
 *
 * `undefined` must not reach the wire at all — the backend runs
 * `forbidNonWhitelisted` and, more importantly, an omitted field means "leave
 * the stored rule alone" on an edit, while an explicit `null` means "remove
 * it". Collapsing the two would silently wipe a rule every time a form that
 * does not know about it saves a schedule.
 */
function serializeMinParticipants(schedule: {
  minParticipants?: number | null
  minDecisionMinutes?: number | null
}): { minParticipants?: number | null; minDecisionMinutes?: number | null } {
  return {
    ...(schedule.minParticipants !== undefined
      ? { minParticipants: schedule.minParticipants }
      : {}),
    ...(schedule.minDecisionMinutes !== undefined
      ? { minDecisionMinutes: schedule.minDecisionMinutes }
      : {}),
  }
}

@Serializable()
export class Schedule extends Fromable {
  @JsonProperty()
  id!: number

  @JsonProperty()
  classId!: number

  @JsonProperty()
  groupId?: string

  @JsonProperty()
  branchId!: number

  @JsonProperty()
  userMembershipId?: number

  @DayjsProperty()
  startTime!: Dayjs

  @DayjsProperty()
  endTime!: Dayjs

  @DayjsProperty()
  cancelledAt?: Dayjs

  @JsonProperty()
  cancelBefore!: number

  @JsonProperty()
  bookingLeadTimeMinutes?: number

  @DayjsProperty()
  publishedAt?: Dayjs | null

  @DayjsProperty()
  bookingOpenAt?: Dayjs | null

  @JsonProperty()
  bookingOpenOffsetMinutes?: number | null

  @JsonProperty()
  price!: number

  @JsonProperty()
  durationMinutes!: number

  @JsonProperty()
  capacity!: number

  @JsonProperty()
  capacityMember?: number

  @JsonProperty()
  capacitySession?: number

  @JsonProperty()
  capacityDropIn?: number

  /** Defaults to PER_TYPE when the backend omits it (pre-migration rows). */
  @JsonProperty()
  capacityMode?: ScheduleCapacityMode

  @JsonProperty()
  isPrivate!: boolean

  @JsonProperty()
  location!: string

  @JsonProperty()
  roomId?: string

  @JsonProperty()
  description!: string

  @JsonProperty()
  level!: string

  @JsonProperty()
  priceMember?: number

  @JsonProperty()
  priceSession?: number

  @JsonProperty()
  priceDropIn?: number

  @JsonProperty()
  chargeTypeMember?: CHARGE_TYPE

  @JsonProperty()
  chargeTypeSession?: CHARGE_TYPE

  @JsonProperty()
  chargeTypeDropIn?: CHARGE_TYPE

  @JsonProperty({ type: ClassInfo })
  class!: ClassInfo

  @JsonProperty({ elementType: Trainer })
  trainers?: Trainer[]

  @JsonProperty({ elementType: Member })
  members?: Member[]

  @JsonProperty({ elementType: ClassBookingItem })
  bookings?: ClassBookingItem[]

  @JsonProperty()
  memberType?: PARTICIPANT_TYPE

  @JsonProperty({ type: RecurringSchedule })
  recurringSchedule?: RecurringSchedule

  @JsonProperty({ type: SingleSession })
  singleSession?: SingleSession

  @JsonProperty({ elementType: Booking })
  booking?: Booking[]

  @JsonProperty('bookingCount')
  bookingCount?: number

  @JsonProperty()
  bookingCountMember?: number

  @JsonProperty()
  bookingCountSession?: number

  @JsonProperty()
  bookingCountDropIn?: number

  @JsonProperty({ elementType: SchedulePricing })
  schedulePricing?: SchedulePricing[]

  @JsonProperty()
  byUserType?: Record<string, ScheduleByUserType>

  /**
   * Minimum-participants rule (§6). `minParticipants` null = the round is never
   * auto-cancelled for being empty; `minDecisionMinutes` is how long before the
   * start the sweep makes the call.
   */
  @JsonProperty()
  minParticipants?: number | null

  @JsonProperty()
  minDecisionMinutes?: number | null

  /** Staff said "teach it anyway" — the sweep leaves this round alone. */
  @JsonProperty()
  keepBelowMinimum?: boolean

  @DayjsProperty()
  minCheckDoneAt?: Dayjs | null

  @DayjsProperty()
  minWarningSentAt?: Dayjs | null

  /** `'MIN_PARTICIPANTS'` when the sweep cancelled this round. */
  @JsonProperty()
  autoCancelReason?: string | null

  get attendanceCount() {
    return this.booking?.length ?? 0
  }

  set attendanceCount(_: number) {
    // ignore incoming writes; attendanceCount is derived from bookings
  }

  public serializeToCreate() {
    return {
      startTime: this.startTime.toISOString(),
      endTime: this.endTime.toISOString(),
      cancelBefore: this.cancelBefore ?? 60,
      bookingLeadTimeMinutes: this.bookingLeadTimeMinutes ?? 0,
      location: this.location,
      capacity: this.capacity,
      capacityMember: this.capacityMember,
      capacitySession: this.capacitySession,
      capacityDropIn: this.capacityDropIn,
      capacityMode: this.capacityMode,
      description: this.description,
      level: this.level,
      trainerIds: this.trainers?.map((trainer) => trainer.id) ?? [],
      publishedAt: this.publishedAt?.toISOString() ?? null,
      price: this.price ?? 0,
      priceMember: this.priceMember,
      priceSession: this.priceSession,
      priceDropIn: this.priceDropIn,
      chargeTypeMember: this.chargeTypeMember,
      chargeTypeSession: this.chargeTypeSession,
      chargeTypeDropIn: this.chargeTypeDropIn,
      roomId: this.roomId,
      memberType: this.memberType,
      isPrivate: this.isPrivate ?? false,
      ...serializeMinParticipants(this),
    }
  }

  public serializeToCreateWithBooking() {
    let session = {}

    if (this.singleSession && Object.keys(this.singleSession).length > 0) {
      session = {
        singleSession: {
          appointmentDate: this.singleSession.appointmentDate.toISOString(),
          startTime: this.singleSession.startTime.format('HH:mm'),
          endTime: this.singleSession.endTime.format('HH:mm'),
        },
      }
    }

    if (this.recurringSchedule && Object.keys(this.recurringSchedule).length > 0) {
      session = {
        recurringSchedule: {
          startDate: this.recurringSchedule.startDate.toISOString(),
          endDate: this.recurringSchedule.endDate.toISOString(),
          singleTimeDurations: this.recurringSchedule.singleTimeDurations.map((duration) => ({
            dayOfWeekIndex: duration.dayOfWeekIndex,
            startTime: duration.startTime.format('HH:mm'),
            endTime: duration.endTime.format('HH:mm'),
            ...(typeof duration.trainerId === 'number' ? { trainerId: duration.trainerId } : {}),
          })),
        },
      }
    }

    return {
      location: this.location,
      capacity: this.capacity,
      capacityMember: this.capacityMember,
      capacitySession: this.capacitySession,
      capacityDropIn: this.capacityDropIn,
      capacityMode: this.capacityMode,
      bookings:
        this.bookings?.map((b) => ({
          memberId: b.memberId,
          userMembershipId: b.userMembershipId,
        })) ?? [],
      trainerIds: this.trainers?.map((trainer) => trainer.id) ?? [],
      price: this.price,
      priceMember: this.priceMember,
      priceSession: this.priceSession,
      priceDropIn: this.priceDropIn,
      chargeTypeMember: this.chargeTypeMember,
      chargeTypeSession: this.chargeTypeSession,
      chargeTypeDropIn: this.chargeTypeDropIn,
      level: this.level,
      memberType: this.memberType,
      isRepeat: !!this.recurringSchedule,
      isPrivate: this.isPrivate || false,
      ...serializeMinParticipants(this),
      ...session,
    }
  }

  public getTimeStatus(): CLASS_SCHEDULE_LIST_STATUS {
    const now = dayjs()
    if (this.cancelledAt) {
      return CLASS_SCHEDULE_LIST_STATUS.CANCELLED
    }

    if (
      dayjs(this.startTime).isAfter(now) &&
      this.publishedAt &&
      dayjs(this.publishedAt).isAfter(now)
    ) {
      return CLASS_SCHEDULE_LIST_STATUS.UPCOMING
    }
    if (dayjs(this.endTime).isBefore(now)) {
      return CLASS_SCHEDULE_LIST_STATUS.COMPLETED
    }

    return CLASS_SCHEDULE_LIST_STATUS.ACTIVE
  }

  public getStatus() {
    const capacityAvailablePercentage =
      ((this.booking?.filter(
        (booking) => booking.status.toLocaleLowerCase() !== 'CANCELLED'.toLocaleLowerCase()
      ).length ?? 0) /
        this.capacity) *
      100

    const rounded =
      capacityAvailablePercentage % 1 >= 0.5
        ? Math.ceil(capacityAvailablePercentage)
        : Math.floor(capacityAvailablePercentage)

    if (rounded >= 100) {
      return 'full'
    } else if (rounded >= 75) {
      return 'almost-full'
    } else {
      return 'open'
    }
  }
}

@Serializable()
export class MemberSchedule extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('userId')
  userId!: number

  @JsonProperty('firstName')
  firstName!: string

  @JsonProperty('lastName')
  lastName!: string

  @JsonProperty('nickname')
  nickname!: string

  @JsonProperty()
  email!: string

  @JsonProperty()
  phone!: string

  @JsonProperty('memberType')
  memberType?: string

  @JsonProperty('status')
  status!: string

  @DayjsProperty()
  bookedAt!: Dayjs

  @JsonProperty('cancelledAt')
  cancelledAt!: string | null

  /** null on rows that were never cancelled, and on pre-migration cancellations. */
  @JsonProperty('cancelKind')
  cancelKind?: ClassCancelKind | null

  @JsonProperty('cancelReason')
  cancelReason?: string | null

  /** True when the cancel deliberately gave nothing back (FORFEIT / staff choice). */
  @JsonProperty('refundSkipped')
  refundSkipped?: boolean

  @JsonProperty('source')
  source?: string

  @JsonProperty('addedByUserId')
  addedByUserId?: number | null

  @JsonProperty('addedByUser')
  addedByUser?: {
    id: number
    firstName: string
    lastName: string
    nickname: string
  } | null

  /** null on rows that involve no money (FREE / USE_SESSION / CREDIT). */
  @JsonProperty('paymentStatus')
  paymentStatus?: CLASS_BOOKING_PAYMENT_STATUS | null

  /** Baht snapshot of the PAID row price at booking time. */
  @JsonProperty('totalPrice')
  totalPrice?: number | null

  @JsonProperty('paymentDueAt')
  paymentDueAt?: string | null

  @JsonProperty('paymentSlipUrl')
  paymentSlipUrl?: string | null

  @JsonProperty('paidWith')
  paidWith?: ClassBookingPaymentMethod | null

  @JsonProperty('paidAt')
  paidAt?: string | null

  @JsonProperty('receiptId')
  receiptId?: number | null

  /** Set when the settlement receipt was voided; `receiptId` is kept for the trail. */
  @JsonProperty('refundedAt')
  refundedAt?: string | null

  @JsonProperty('slipVerification')
  slipVerification?: ClassBookingSlipVerification | null

  /** Pick-a-spot rooms only — null everywhere else. */
  @JsonProperty('spotId')
  spotId?: string | null

  @JsonProperty('spot')
  spot?: ClassBookingSpot | null

  public getStatus() {
    if (this.status.toLowerCase() === 'booked') {
      return 'Booked'
    } else if (this.status.toLowerCase() === 'cancelled') {
      return 'Cancelled'
    } else if (this.status.toLowerCase() === 'present') {
      return 'Joined'
    } else if (this.status.toLowerCase() === 'absent') {
      return 'Not Participating'
    }
    return this.status
  }

  public isWalkIn(): boolean {
    return this.source === 'WALK_IN'
  }

  public addedByDisplayName(): string | null {
    if (!this.addedByUser) return null
    const parts = [this.addedByUser.firstName, this.addedByUser.lastName].filter(Boolean)
    return parts.length ? parts.join(' ') : this.addedByUser.nickname || null
  }
}

export type CreateRecurringClassSchedulePayload = {
  startDate: string
  /** Omitted when noEndDate is set — the backend then generates a fixed 6-month horizon. */
  endDate?: string
  noEndDate?: boolean
  daysOfWeek: number[]
  startTime: string
  endTime: string
  location: string
  roomId?: string
  level: string
  cancelBefore: number
  bookingLeadTimeMinutes?: number
  trainerIds: number[]
  capacity?: number
  capacityMember?: number
  capacitySession?: number
  capacityDropIn?: number
  capacityMode?: ScheduleCapacityMode
  description?: string
  bookingOpenAt?: string | null
  bookingOpenOffsetMinutes?: number
  price?: number
  priceMember?: number
  priceSession?: number
  priceDropIn?: number
  chargeTypeMember?: CHARGE_TYPE
  chargeTypeSession?: CHARGE_TYPE
  chargeTypeDropIn?: CHARGE_TYPE
  memberType?: string
  isPrivate?: boolean
  /** §6 — copied onto every generated round; omitted takes the class defaults. */
  minParticipants?: number | null
  minDecisionMinutes?: number | null
}

@Serializable()
export class ClassBooking extends Fromable {
  @JsonProperty()
  id!: number

  @JsonProperty()
  userId!: number

  @JsonProperty()
  scheduleId!: number

  @JsonProperty()
  status!: string

  @JsonProperty()
  bookedAt!: string

  @JsonProperty()
  cancelledAt!: string | null

  @JsonProperty()
  cancelKind?: ClassCancelKind | null

  @JsonProperty()
  cancelReason?: string | null

  @JsonProperty()
  refundSkipped?: boolean

  @JsonProperty()
  userMembershipId!: number | null

  @JsonProperty()
  paymentStatus?: CLASS_BOOKING_PAYMENT_STATUS | null

  @JsonProperty()
  totalPrice?: number | null

  @JsonProperty()
  paymentDueAt?: string | null

  @JsonProperty()
  paymentSlipUrl?: string | null

  @JsonProperty()
  paidWith?: ClassBookingPaymentMethod | null

  @JsonProperty()
  paidAt?: string | null

  @JsonProperty()
  receiptId?: number | null

  @JsonProperty()
  refundedAt?: string | null

  @JsonProperty()
  slipVerification?: ClassBookingSlipVerification | null

  /** Pick-a-spot rooms only — null everywhere else. */
  @JsonProperty()
  spotId?: string | null

  @JsonProperty()
  spot?: ClassBookingSpot | null
}

/** i18n key for a booking payment status chip. */
export const CLASS_BOOKING_PAYMENT_STATUS_KEY: Record<CLASS_BOOKING_PAYMENT_STATUS, string> = {
  [CLASS_BOOKING_PAYMENT_STATUS.PENDING]: 'class-payment-status-pending',
  [CLASS_BOOKING_PAYMENT_STATUS.VERIFYING]: 'class-payment-status-verifying',
  [CLASS_BOOKING_PAYMENT_STATUS.PAID]: 'class-payment-status-paid',
  [CLASS_BOOKING_PAYMENT_STATUS.REJECTED]: 'class-payment-status-rejected',
  [CLASS_BOOKING_PAYMENT_STATUS.CANCELLED]: 'class-payment-status-cancelled',
  [CLASS_BOOKING_PAYMENT_STATUS.REFUNDED]: 'class-payment-status-refunded',
}

/**
 * Chip colour for a booking payment status, matching the StatusLabel palette.
 * `neutral` is not a StatusLabel `status` value — it rides the explicit `color`
 * prop instead (see `classBookingPaymentChipProps`).
 */
export const CLASS_BOOKING_PAYMENT_STATUS_TONE: Record<
  CLASS_BOOKING_PAYMENT_STATUS,
  'success' | 'error' | 'warning' | 'info' | 'neutral'
> = {
  [CLASS_BOOKING_PAYMENT_STATUS.PENDING]: 'warning',
  [CLASS_BOOKING_PAYMENT_STATUS.VERIFYING]: 'info',
  [CLASS_BOOKING_PAYMENT_STATUS.PAID]: 'success',
  [CLASS_BOOKING_PAYMENT_STATUS.REJECTED]: 'error',
  [CLASS_BOOKING_PAYMENT_STATUS.CANCELLED]: 'error',
  // A refund is neither good news nor an error — it is bookkeeping.
  [CLASS_BOOKING_PAYMENT_STATUS.REFUNDED]: 'neutral',
}

/** StatusLabel props for a payment status chip, neutral tones included. */
export function classBookingPaymentChipProps(status: CLASS_BOOKING_PAYMENT_STATUS): {
  status?: 'success' | 'error' | 'warning' | 'info'
  color?: string
} {
  const tone = CLASS_BOOKING_PAYMENT_STATUS_TONE[status]
  return tone === 'neutral' ? { color: 'text.secondary' } : { status: tone }
}

/**
 * Whether staff can still take money for this booking. REFUNDED counts: the
 * receipt was voided but the seat was kept, so the member may pay again.
 */
export function isClassBookingPaymentSettleable(
  status?: CLASS_BOOKING_PAYMENT_STATUS | string | null
): boolean {
  return (
    status === CLASS_BOOKING_PAYMENT_STATUS.PENDING ||
    status === CLASS_BOOKING_PAYMENT_STATUS.VERIFYING ||
    status === CLASS_BOOKING_PAYMENT_STATUS.REJECTED ||
    status === CLASS_BOOKING_PAYMENT_STATUS.REFUNDED
  )
}

/**
 * Whether the approve/reject buttons belong on the slip viewer. A REFUNDED
 * booking is settleable but its slip verdict is history — re-approving it would
 * issue a second receipt for a transfer that was already given back.
 */
export function isClassBookingSlipDecidable(
  status?: CLASS_BOOKING_PAYMENT_STATUS | string | null
): boolean {
  return (
    status === CLASS_BOOKING_PAYMENT_STATUS.PENDING ||
    status === CLASS_BOOKING_PAYMENT_STATUS.VERIFYING ||
    status === CLASS_BOOKING_PAYMENT_STATUS.REJECTED
  )
}

/**
 * Chip colour for a cancel-kind chip. A late cancel is the only one that costs
 * the member something, so it is the only one that reads as a warning; the rest
 * are bookkeeping and stay neutral.
 */
const CLASS_CANCEL_KIND_TONE: Record<ClassCancelKind, 'warning' | 'neutral'> = {
  [CLASS_CANCEL_KIND.ON_TIME]: 'neutral',
  [CLASS_CANCEL_KIND.LATE]: 'warning',
  [CLASS_CANCEL_KIND.STAFF]: 'neutral',
  [CLASS_CANCEL_KIND.SYSTEM]: 'neutral',
}

/**
 * StatusLabel props for a cancel-kind chip, or null when there is nothing to
 * show. Takes a plain string: the value arrives straight off an API response,
 * and every caller would otherwise have to cast it.
 */
export function classCancelKindChipProps(kind?: string | null): {
  labelKey: string
  status?: 'warning'
  color?: string
} | null {
  if (!kind) return null
  const labelKey = CLASS_CANCEL_KIND_KEY[kind as ClassCancelKind]
  // An unknown kind is not a chip we can name. Showing the raw enum value would
  // put "FOO_BAR" in front of staff; showing nothing loses no information they
  // could have acted on.
  if (!labelKey) return null
  const tone = CLASS_CANCEL_KIND_TONE[kind as ClassCancelKind]
  return tone === 'warning'
    ? { labelKey, status: 'warning' }
    : { labelKey, color: 'text.secondary' }
}
